import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Tooltip,
  Paper,
  Collapse,
} from '@mui/material';
import {
  TextFieldsRounded,
  TitleRounded,
  ImageRounded,
  SmartButtonRounded,
  HorizontalRuleRounded,
  HeightRounded,
  FormatQuoteRounded,
  KeyboardArrowUpRounded,
  KeyboardArrowDownRounded,
  DeleteOutlineRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
} from '@mui/icons-material';
import type {
  EmailBlock,
  BlockType,
  TextBlock,
  HeadingBlock,
  ImageBlock,
  ButtonBlock,
  SpacerBlock,
  QuoteBlock,
} from './types';
import { createDefaultBlock } from './utils';
import { ImagePickerDialog } from '../media/ImagePickerDialog';
import { useTranslation } from 'react-i18next';

// ─── Block type catalog ───────────────────────────────────────────────────────

// EL NOMBRE DEL BLOQUE LO LEE EL USUARIO, así que va por i18n aunque esté
// escrito como propiedad de un objeto y no dentro de JSX. La regla del proyecto
// define el alcance por QUIÉN LEE, no por dónde está escrito: un rótulo en una
// constante se lee igual que uno en un botón.
const BLOCK_TYPES: { type: BlockType; icon: React.ReactNode }[] = [
  { type: 'text', icon: <TextFieldsRounded fontSize="small" /> },
  { type: 'heading', icon: <TitleRounded fontSize="small" /> },
  { type: 'image', icon: <ImageRounded fontSize="small" /> },
  { type: 'button', icon: <SmartButtonRounded fontSize="small" /> },
  { type: 'divider', icon: <HorizontalRuleRounded fontSize="small" /> },
  { type: 'spacer', icon: <HeightRounded fontSize="small" /> },
  { type: 'quote', icon: <FormatQuoteRounded fontSize="small" /> },
];

// ─── Individual block editor ──────────────────────────────────────────────────

interface BlockItemProps {
  block: EmailBlock;
  index: number;
  total: number;
  tenantId: string;
  /** External override: when set, forces expand (true) or collapse (false). */
  forceExpanded: boolean | undefined;
  onChange: (updated: EmailBlock) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function BlockItem({
  block,
  index,
  total,
  tenantId,
  forceExpanded,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: BlockItemProps) {
  const { t } = useTranslation('sis');
  // Default collapsed; override propagates from parent toggle.
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Sync with external forceExpanded when it changes
  useEffect(() => {
    if (forceExpanded !== undefined) {
      setExpanded(forceExpanded);
    }
  }, [forceExpanded]);

  const label = t(`campaign.block.types.${block.type}`, { defaultValue: block.type });
  const icon = BLOCK_TYPES.find((b) => b.type === block.type)?.icon;

  function field(bl: EmailBlock): React.ReactNode {
    switch (bl.type) {
      case 'text': {
        const b = bl as TextBlock;
        return (
          <Stack spacing={2}>
            <TextField
              label={t('campaign.block.content')}
              value={b.content}
              onChange={(e) => onChange({ ...b, content: e.target.value })}
              multiline
              minRows={3}
              fullWidth
              size="small"
              helperText={t('campaign.block.personalizeHelp')}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t('campaign.block.align')}</InputLabel>
              <Select
                value={b.align}
                label={t('campaign.block.align')}
                onChange={(e) => onChange({ ...b, align: e.target.value as TextBlock['align'] })}
              >
                <MenuItem value="left">{t('campaign.block.alignLeft')}</MenuItem>
                <MenuItem value="center">{t('campaign.block.alignCenter')}</MenuItem>
                <MenuItem value="right">{t('campaign.block.alignRight')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        );
      }
      case 'heading': {
        const b = bl as HeadingBlock;
        return (
          <Stack spacing={2}>
            <TextField
              label={t('campaign.block.heading')}
              value={b.content}
              onChange={(e) => onChange({ ...b, content: e.target.value })}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {t('campaign.block.color')}
              </Typography>
              <input
                type="color"
                value={b.color}
                onChange={(e) => onChange({ ...b, color: e.target.value })}
                style={{
                  height: 36,
                  width: 48,
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              />
            </Stack>
          </Stack>
        );
      }
      case 'image': {
        const b = bl as ImageBlock;
        return (
          <Stack spacing={2}>
            <TextField
              label={t('campaign.block.imageUrl')}
              value={b.url}
              onChange={(e) => onChange({ ...b, url: e.target.value })}
              fullWidth
              size="small"
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<ImageRounded />}
              onClick={() => setPickerOpen(true)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: 2 }}
            >
              {t('campaign.block.pickFromLibrary')}
            </Button>
            <ImagePickerDialog
              open={pickerOpen}
              tenantId={tenantId}
              onSelect={(url) => {
                onChange({ ...b, url });
                setPickerOpen(false);
              }}
              onClose={() => setPickerOpen(false)}
            />
            <TextField
              label={t('campaign.block.imageAlt')}
              value={b.alt}
              onChange={(e) => onChange({ ...b, alt: e.target.value })}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Ancho: {b.width}%
                </Typography>
                <Slider
                  value={b.width}
                  onChange={(_, v) => onChange({ ...b, width: v as number })}
                  min={20}
                  max={100}
                  step={5}
                  valueLabelDisplay="auto"
                  size="small"
                />
              </Box>
            </Stack>
          </Stack>
        );
      }
      case 'button': {
        const b = bl as ButtonBlock;
        return (
          <Stack spacing={2}>
            <TextField
              label={t('campaign.block.buttonText')}
              value={b.label}
              onChange={(e) => onChange({ ...b, label: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label={t('campaign.block.buttonUrl')}
              value={b.url}
              onChange={(e) => onChange({ ...b, url: e.target.value })}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {t('campaign.block.buttonColor')}
              </Typography>
              <input
                type="color"
                value={b.color}
                onChange={(e) => onChange({ ...b, color: e.target.value })}
                style={{
                  height: 36,
                  width: 48,
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              />
            </Stack>
          </Stack>
        );
      }
      case 'divider':
        return (
          <Typography variant="caption" color="text.secondary">
            {t('campaign.block.dividerNoOptions')}
          </Typography>
        );
      case 'spacer': {
        const b = bl as SpacerBlock;
        return (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Altura del espaciado: {b.height}px
            </Typography>
            <Slider
              value={b.height}
              onChange={(_, v) => onChange({ ...b, height: v as number })}
              min={4}
              max={80}
              step={4}
              valueLabelDisplay="auto"
              size="small"
            />
          </Stack>
        );
      }
      case 'quote': {
        const b = bl as QuoteBlock;
        return (
          <Stack spacing={2}>
            <TextField
              label={t('campaign.block.quote')}
              value={b.content}
              onChange={(e) => onChange({ ...b, content: e.target.value })}
              multiline
              minRows={2}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {t('campaign.block.accentColor')}
              </Typography>
              <input
                type="color"
                value={b.accentColor}
                onChange={(e) => onChange({ ...b, accentColor: e.target.value })}
                style={{
                  height: 36,
                  width: 48,
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              />
            </Stack>
          </Stack>
        );
      }
      default:
        return null;
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        borderColor: 'divider',
        '&:hover': { borderColor: 'primary.main' },
        transition: 'border-color 0.15s',
      }}
    >
      {/* Block header — click to toggle */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', cursor: 'pointer' }}
        onClick={() => setExpanded((e) => !e)}
      >
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {label}
        </Typography>
        <Tooltip title={t('campaign.block.moveUp')}>
          <span>
            {/* EL RÓTULO VA EN EL BOTÓN, no sólo en el `Tooltip`. El tooltip
                es ayuda visual: quien navega con lector de pantalla o con el
                teclado no lo recibe, y un botón que sólo tiene un icono se
                anuncia como «botón» a secas. */}
            <IconButton
              size="small"
              aria-label={t('campaign.block.moveUp')}
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp();
              }}
              disabled={index === 0}
            >
              <KeyboardArrowUpRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('campaign.block.moveDown')}>
          <span>
            <IconButton
              size="small"
              aria-label={t('campaign.block.moveDown')}
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown();
              }}
              disabled={index === total - 1}
            >
              <KeyboardArrowDownRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('campaign.block.remove')}>
          <IconButton
            size="small"
            aria-label={t('campaign.block.remove')}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            color="error"
          >
            <DeleteOutlineRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* ESTE NO TENÍA NI TOOLTIP NI RÓTULO: se anunciaba como «botón» y
            nada más, así que con teclado no había forma de saber que pliega los
            ajustes del bloque. `aria-expanded` además dice en qué estado está. */}
        <IconButton
          size="small"
          aria-label={expanded ? t('campaign.block.collapse') : t('campaign.block.expand')}
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((ex) => !ex);
          }}
        >
          {expanded ? (
            <ExpandLessRounded fontSize="small" />
          ) : (
            <ExpandMoreRounded fontSize="small" />
          )}
        </IconButton>
      </Stack>

      {/* Block fields */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>{field(block)}</Box>
      </Collapse>
    </Paper>
  );
}

// ─── BlockEditor ──────────────────────────────────────────────────────────────

interface BlockEditorProps {
  blocks: EmailBlock[];
  primaryColor: string;
  tenantId: string;
  /** Controlled from parent: null = each block uses own state; true/false = forced */
  allExpanded: boolean | null;
  onChange: (blocks: EmailBlock[]) => void;
}

export function BlockEditor({
  blocks,
  primaryColor,
  tenantId,
  allExpanded,
  onChange,
}: BlockEditorProps) {
  const { t } = useTranslation('sis');
  // Increment to force BlockItem key change on toggle so useEffect fires reliably
  const [toggleGen, setToggleGen] = useState(0);

  // When allExpanded changes from parent, bump the generation counter
  useEffect(() => {
    if (allExpanded !== null) setToggleGen((g) => g + 1);
  }, [allExpanded]);

  function updateBlock(index: number, updated: EmailBlock) {
    const next = [...blocks];
    next[index] = updated;
    onChange(next);
  }

  function deleteBlock(index: number) {
    onChange(blocks.filter((_, i) => i !== index));
  }

  function moveBlock(index: number, direction: 'up' | 'down') {
    const next = [...blocks];
    const swap = direction === 'up' ? index - 1 : index + 1;
    const temp = next[index];
    next[index] = next[swap]!;
    next[swap] = temp!;
    onChange(next);
  }

  function addBlock(type: BlockType) {
    onChange([...blocks, createDefaultBlock(type, primaryColor)]);
  }

  return (
    <Stack spacing={2}>
      {/* Existing blocks */}
      {blocks.map((block, index) => (
        <BlockItem
          key={`${block.id}-gen${toggleGen}`}
          block={block}
          index={index}
          total={blocks.length}
          tenantId={tenantId}
          forceExpanded={allExpanded ?? undefined}
          onChange={(updated) => updateBlock(index, updated)}
          onDelete={() => deleteBlock(index)}
          onMoveUp={() => moveBlock(index, 'up')}
          onMoveDown={() => moveBlock(index, 'down')}
        />
      ))}

      {/* Add block toolbar */}
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1.5}
          fontWeight={600}
        >
          {t('campaign.block.add')}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {BLOCK_TYPES.map((bt) => (
            <Button
              key={bt.type}
              size="small"
              variant="outlined"
              startIcon={bt.icon}
              onClick={() => addBlock(bt.type)}
              sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12 }}
            >
              {t(`campaign.block.types.${bt.type}`, { defaultValue: bt.type })}
            </Button>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}
